import React, { useEffect, useState, useCallback, useRef } from "react";
import { axiosMaster } from "../api/axiosInstance";

const AddEditModuleMaster = ({ editData, onClose, onSuccess }) => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [moduleName, setModuleName] = useState("");
  const [category, setCategory] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= REFS ================= */

  const moduleRef = useRef();
  const categoryRef = useRef();
  const orderRef = useRef();
  const saveRef = useRef();

  /* ================= TITLE CASE ================= */

  const toTitleCase = (text = "") =>
    text
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  /* ================= LOAD EDIT ================= */

  useEffect(() => {
    if (editData) {
      setModuleName(editData.moduleName || "");
      setCategory(editData.category || "");
      setDisplayOrder(editData.displayOrder || "");
    }

    setTimeout(() => {
      moduleRef.current?.focus();
    }, 100);
  }, [editData]);

  /* ================= SUBMIT ================= */

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    const formattedName = toTitleCase(moduleName.trim());

    if (!formattedName) {
      alert("Enter module name");
      moduleRef.current.focus();
      return;
    }

    try {
      setLoading(true);

      const payload = {
        moduleId: editData?.moduleId || 0,
        moduleName: formattedName,
        category: category.trim(),
        displayOrder: Number(displayOrder) || 0,
        loginUserId: storedUser?.userId || 0,
      };

      if (editData?.moduleId) {
        await axiosMaster.put("/ModuleMaster/Update", payload);
      } else {
        await axiosMaster.post("/ModuleMaster/Create", payload);
      }

      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Save module error", err);
    } finally {
      setLoading(false);
    }
  }, [moduleName, category, displayOrder, editData, storedUser?.userId, loading, onClose, onSuccess]);

  /* ================= KEYBOARD NAVIGATION ================= */

  const handleKey = (e, nextRef, prevRef) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (nextRef) {
        nextRef.current?.focus();
      } else {
        handleSubmit();
      }
    }

    if (e.key === "Escape") {
      e.preventDefault();

      if (prevRef) {
        prevRef.current?.focus();
      } else {
        onClose?.();
      }
    }
  };

  /* ================= UI ================= */

  return (
    <div
      className="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-[420px] p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">
          {editData ? "Edit Module" : "Add Module"}
        </h3>

        {/* MODULE NAME */}

        <input
          ref={moduleRef}
          type="text"
          value={moduleName}
          onChange={(e) => setModuleName(e.target.value)}
          onBlur={() => setModuleName(toTitleCase(moduleName))}
          onKeyDown={(e) => handleKey(e, categoryRef, null)}
          placeholder="Module Name"
          className="w-full border px-3 py-2 rounded-lg mb-3"
        />

        {/* CATEGORY */}

        <input
          ref={categoryRef}
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          onKeyDown={(e) => handleKey(e, orderRef, moduleRef)}
          placeholder="Category"
          className="w-full border px-3 py-2 rounded-lg mb-3"
        />

        {/* DISPLAY ORDER */}

        <input
          ref={orderRef}
          type="number"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
          onKeyDown={(e) => handleKey(e, saveRef, categoryRef)}
          placeholder="Display Order"
          className="w-full border px-3 py-2 rounded-lg mb-4"
        />

        {/* BUTTONS */}

        <div className="flex justify-end gap-2">

          <button
            onClick={onClose}
            className="px-3 py-2 border rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            ref={saveRef}
            onClick={handleSubmit}
            onKeyDown={(e) => handleKey(e, null, orderRef)}
            disabled={loading}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>

        </div>
      </div>
    </div>
  );
};

export default AddEditModuleMaster;